import React, { useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ModernBarChart,
    ModernDonutChart,
    PeakHoursChart,
    BarChartDataPoint,
    DonutChartSegment,
    HourlySalesPoint
} from './components/analytics-charts';
import { exportExcel } from '../../utils/util';
import { fetchAnalyticsReport } from '../../api/report';
import { AnalyticsReport } from '../../model/report';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

const ReportVisualAnalytics: React.FC = () => {
    const [viewMode, setViewMode] = useState<'weekly' | 'monthly'>('weekly');
    const breakdownPagination = useTablePagination();
    const { ref: breakdownRef, y: breakdownScrollY } = useMeasuredRowScrollY(420);

    // Every series below comes from Backend /api/v1/report ANALYTICS. Until
    // 2026-09-08 all six were hardcoded here -- a week of invented daily
    // sales, a year of invented months, five invented years, and two
    // invented donuts -- so the first tab of the reports screen showed a
    // shop that does not exist. `null` = not loaded or failed; the charts
    // then show nothing and the notice below says why.
    const [report, setReport] = useState<AnalyticsReport | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchAnalyticsReport();
                if (!cancelled) { setReport(data); setLoadError(null); }
            } catch (e: any) {
                if (!cancelled) {
                    setReport(null);
                    setLoadError(e?.response?.data?.message || e?.message || 'โหลดข้อมูลวิเคราะห์ไม่สำเร็จ');
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const weeklyData: BarChartDataPoint[] = report?.weeklyData ?? [];
    const monthlyData: BarChartDataPoint[] = report?.monthlyData ?? [];
    const paymentDonutData: DonutChartSegment[] = report?.paymentDonutData ?? [];
    const categoryDonutData: DonutChartSegment[] = report?.categoryDonutData ?? [];
    const hourlyData: HourlySalesPoint[] = report?.hourlyData ?? [];
    const thisYear = new Date().getFullYear();

    // KPI Metrics calculation
    const activeData = viewMode === 'weekly'
        ? weeklyData
        : monthlyData.filter(m => m.value > 0);

    const totalSales = activeData.reduce((acc, d) => acc + d.value, 0);
    const totalPrevSales = activeData.reduce((acc, d) => acc + (d.compareValue || 0), 0);
    const totalBills = activeData.reduce((acc, d) => acc + (d.bills || 0), 0);
    const growthPercent = totalPrevSales > 0 ? Number((((totalSales - totalPrevSales) / totalPrevSales) * 100).toFixed(1)) : 0;
    const avgPerBill = totalBills > 0 ? Math.round(totalSales / totalBills) : 0;
    // Loading is its own state: "no sales in this period" is a finding,
    // and stating it before the answer has arrived would be a guess.
    const loading = report === null && loadError === null;
    const dash = '…';

    // The best period in the data on screen -- not a remembered one.
    const peak = activeData.reduce<BarChartDataPoint | null>(
        (best, d) => (best === null || d.value > best.value) ? d : best, null);
    const peakText = loading ? dash
        : loadError ? 'ดึงข้อมูลไม่สำเร็จ'
            : peak && peak.value > 0
                ? `${peak.label} (฿${peak.value.toLocaleString()})`
                : 'ยังไม่มียอดขายในช่วงนี้';

    // Main payment channel, from the donut Backend returned. This card
    // read "สแกน QR (58%)" and "เงินสดรองลงมา 31%" as literals until
    // 2026-09-08 -- percentages of a shop that does not exist.
    const paymentsByValue = [...paymentDonutData].sort((a, b) => b.value - a.value);
    const topPayment = paymentsByValue[0];
    const secondPayment = paymentsByValue[1];

    // Table Columns for Breakdown Table
    const breakdownColumns: ColumnsType<BarChartDataPoint> = [
        {
            title: viewMode === 'weekly' ? 'วันในสัปดาห์' : 'เดือน',
            dataIndex: 'label',
            key: 'label',
            render: (val: string) => <span style={{ fontWeight: 700 }}>{val}</span>
        },
        {
            title: 'ยอดขายปัจจุบัน',
            dataIndex: 'value',
            key: 'value',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'ยอดขายช่วงก่อนหน้า',
            dataIndex: 'compareValue',
            key: 'compareValue',
            render: (val?: number) => val ? `฿${val.toLocaleString()}` : '-'
        },
        {
            title: 'การเติบโต (%)',
            key: 'growth',
            render: (_: any, record: BarChartDataPoint) => {
                if (!record.compareValue || record.value === 0) return '-';
                const diff = (((record.value - record.compareValue) / record.compareValue) * 100).toFixed(1);
                const isPos = Number(diff) >= 0;
                return (
                    <Tag color={isPos ? 'green' : 'red'}>
                        {isPos ? `+${diff}%` : `${diff}%`}
                    </Tag>
                );
            }
        },
        {
            title: 'จำนวนบิล',
            dataIndex: 'bills',
            key: 'bills',
            render: (val?: number) => `${val || 0} บิล`
        },
        {
            title: 'เฉลี่ยต่อบิล',
            key: 'avgBill',
            render: (_: any, record: BarChartDataPoint) => {
                if (!record.bills || record.bills === 0) return '-';
                return `฿${Math.round(record.value / record.bills).toLocaleString()}`;
            }
        }
    ];

    const handleExportExcel = () => {
        const exportData = activeData.map(d => ({
            'ช่วงเวลา': d.label,
            'ยอดขายปัจจุบัน': d.value,
            'ยอดขายช่วงก่อนหน้า': d.compareValue || 0,
            'จำนวนบิล': d.bills || 0,
            'เฉลี่ยต่อบิล': d.bills ? Math.round(d.value / d.bills) : 0
        }));
        exportExcel(`รายงานวิเคราะห์สถิติ_${viewMode === 'weekly' ? 'รายสัปดาห์' : 'รายเดือน'}`, exportData);
    };

    return (
        <div className="visual-analytics-container">
            {/* View Mode Switcher Header */}
            <div className="analytics-toolbar-card">
                <div className="analytics-toolbar-left">
                    <div className="analytics-view-title">
                        <FontAwesomeIcon icon="chart-simple" style={{ color: 'var(--primary, #FF7A59)', fontSize: '20px' }} />
                        <span>วิเคราะห์สถิติยอดขายเชิงลึก (Sales Analytics)</span>
                    </div>
                    <span className="analytics-view-subtitle">
                        เลือกมุมมองเพื่อดูแนวโน้มการเติบโต สัดส่วนรายได้ และช่วงเวลาขายดี
                    </span>
                </div>

                <div className="analytics-view-switcher">
                    <button
                        type="button"
                        className={`switcher-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                        onClick={() => setViewMode('weekly')}
                    >
                        <FontAwesomeIcon icon="calendar-week" /> สรุปรายสัปดาห์ (Weekly)
                    </button>
                    <button
                        type="button"
                        className={`switcher-btn ${viewMode === 'monthly' ? 'active' : ''}`}
                        onClick={() => setViewMode('monthly')}
                    >
                        <FontAwesomeIcon icon="calendar-days" /> สรุปรายเดือน (Monthly)
                    </button>
                </div>
            </div>

            {loadError && (
                <div className="analytics-load-error" role="alert" style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontWeight: 600 }}>
                    ⚠ {loadError}
                </div>
            )}

            {/* Growth & KPI Cards */}
            <div className="report-kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-sales">
                        <FontAwesomeIcon icon="sack-dollar" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">
                            ยอดขายรวม {viewMode === 'weekly' ? 'สัปดาห์นี้' : `ปี ${thisYear}`}
                        </span>
                        <span className="kpi-value" style={{ color: 'var(--primary, #FF7A59)' }}>
                            {loading ? dash : `฿${totalSales.toLocaleString()}`}
                        </span>
                        <span className={`growth-badge ${growthPercent >= 0 ? 'positive' : 'negative'}`}>
                            {loading ? dash : `${growthPercent >= 0 ? '▲ +' : '▼ '}${growthPercent}% เทียบ${viewMode === 'weekly' ? 'สัปดาห์ก่อน' : 'ปีก่อน'}`}
                        </span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-bills">
                        <FontAwesomeIcon icon="receipt" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">จำนวนบิลทั้งหมด</span>
                        <span className="kpi-value">{loading ? dash : `${totalBills.toLocaleString()} บิล`}</span>
                        <span className="kpi-subtext">
                            {loading ? 'กำลังโหลด' : `เฉลี่ย ฿${avgPerBill.toLocaleString()} ต่อบิล`}
                        </span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-profit">
                        <FontAwesomeIcon icon="trophy" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ช่วงที่มียอดขายสูงสุด (Peak)</span>
                        <span className="kpi-value" style={{ fontSize: '18px', color: '#10B981' }}>
                            {peakText}
                        </span>
                        <span className="kpi-subtext">ยอดสูงสุดของรอบ</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-items">
                        <FontAwesomeIcon icon="credit-card" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ช่องทางชำระเงินหลัก</span>
                        <span className="kpi-value" style={{ fontSize: '18px' }} data-testid="analytics-top-payment">
                            {loading ? dash
                                : topPayment ? `${topPayment.label} (${topPayment.percentage}%)`
                                    : 'ยังไม่มีข้อมูล'}
                        </span>
                        <span className="kpi-subtext">
                            {loading ? 'กำลังโหลด'
                                : secondPayment ? `${secondPayment.label}รองลงมา ${secondPayment.percentage}%`
                                    : 'ไม่มีช่องทางอื่น'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Bar Chart Section */}
            <div style={{ marginBottom: '24px' }}>
                {viewMode === 'weekly' ? (
                    <ModernBarChart
                        data={weeklyData}
                        title="📈 กราฟแท่งเปรียบเทียบยอดขายรายสัปดาห์ (Weekly Sales Comparison)"
                        subtitle="เปรียบเทียบยอดขาย 7 วันในสัปดาห์นี้ เทียบกับสัปดาห์ที่แล้ว"
                        showComparison={true}
                        primaryLabel="สัปดาห์นี้"
                        compareLabel="สัปดาห์ก่อน"
                        height={280}
                    />
                ) : (
                    <ModernBarChart
                        data={monthlyData}
                        title="📊 กราฟแท่งยอดขายรายเดือนในรอบปี (Monthly Sales Trend)"
                        subtitle={`สถิติยอดขาย 12 เดือนประจำปี ${thisYear} พร้อมเปรียบเทียบกับปีก่อนหน้า`}
                        showComparison={true}
                        primaryLabel={`ปี ${thisYear}`}
                        compareLabel={`ปี ${thisYear - 1}`}
                        height={280}
                    />
                )}
            </div>

            {/* 2 Donut Charts Grid */}
            <div className="analytics-two-col-grid" style={{ marginBottom: '24px' }}>
                <ModernDonutChart
                    data={paymentDonutData}
                    title="🍩 กราฟวงกลมสัดส่วนการชำระเงิน (Payment Methods)"
                    subtitle="สัดส่วนรายได้แยกตามประเภทการชำระเงินของลูกค้า"
                    centerLabel="ยอดรับรวม"
                    centerValue={`฿${totalSales.toLocaleString()}`}
                />
                <ModernDonutChart
                    data={categoryDonutData}
                    title="🥧 กราฟวงกลมสัดส่วนยอดขายตามหมวดหมู่ (Category Share)"
                    subtitle="สัดส่วนรายได้จากหมวดหมู่สินค้าในร้านค้า"
                    centerLabel="ยอดขายสินค้า"
                    centerValue={`฿${totalSales.toLocaleString()}`}
                />
            </div>

            {/* Peak Hours Hourly Chart */}
            <div style={{ marginBottom: '24px' }}>
                <PeakHoursChart data={hourlyData} />
            </div>

            {/* Detailed Breakdown Table */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="table-list" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>ตารางแจกแจงสถิติละเอียด ({viewMode === 'weekly' ? 'รายสัปดาห์' : 'รายเดือน'})</span>
                    </span>
                    <button
                        type="button"
                        className="btn-report-action btn-excel"
                        onClick={handleExportExcel}
                    >
                        <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                    </button>
                </div>

                <div ref={breakdownRef}>
                <Table
                    columns={breakdownColumns}
                    dataSource={activeData}
                    rowKey="label"
                    pagination={breakdownPagination}
                    scroll={{ y: breakdownScrollY }}
                />
                </div>
            </div>
        </div>
    );
};

export default ReportVisualAnalytics;
